import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Checkbox,
  Alert
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useCreateCampaignMutation, CampaignType, useMediaAccountsQuery, Platform } from 'graphql/generated/graphql'
import { pages } from 'constants/pages'
import { Target, Users, Settings, Trash2, Plus, TrendingUp, Clock, Sparkles, Link as LinkIcon } from 'lucide-react'


export default function NewCompetitorCampaign() {
  const navigate = useNavigate()
  const [createCampaign, { loading }] = useCreateCampaignMutation()  
  const [campaignName, setCampaignName] = useState('')
  const [competitors, setCompetitors] = useState<Array<{ platform: Platform; username: string }>>([])
  const [newCompetitor, setNewCompetitor] = useState({ platform: Platform.Instagram, username: '' })
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(360)
  const [contentAnalysisEnabled, setContentAnalysisEnabled] = useState(true)

  const [selectedUserAccounts, setSelectedUserAccounts] = useState<number[]>([])
  const competitorPlatforms = Array.from(new Set(competitors.map(c => c.platform)))
  

  const handleToggleUserAccount = (accountId: number) => {
    setSelectedUserAccounts(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    )
  }
  
  const handleAddCompetitor = () => {
    if (newCompetitor.username.trim()) {
      setCompetitors([...competitors, { ...newCompetitor }])
      setNewCompetitor({ platform: Platform.Instagram, username: '' })
    }
  }

  const handleRemoveCompetitor = (index: number) => {
    setCompetitors(competitors.filter((_, i) => i !== index))
  }

  const instagramAccounts = useMediaAccountsQuery({
    variables: { platform: Platform.Instagram },
    skip: !competitorPlatforms.includes(Platform.Instagram)
  })
  
  const tiktokAccounts = useMediaAccountsQuery({
    variables: { platform: Platform.TikTok },
    skip: !competitorPlatforms.includes(Platform.TikTok)
  })
  
  const youtubeAccounts = useMediaAccountsQuery({
    variables: { platform: Platform.Youtube },
    skip: !competitorPlatforms.includes(Platform.Youtube)
  })
  
  const allMediaAccounts = [
    ...(instagramAccounts.data?.mediaAccounts ?? []),
    ...(tiktokAccounts.data?.mediaAccounts ?? []),
    ...(youtubeAccounts.data?.mediaAccounts ?? [])
  ]
  
  const accountsLoading = 
    instagramAccounts.loading || 
    tiktokAccounts.loading || 
    youtubeAccounts.loading

// Filter to only relevant platforms (already filtered by skip, but just in case)
const relevantUserAccounts = allMediaAccounts.filter(
  account => competitorPlatforms.includes(account.platform)
)
console.log('Competitors:', competitors)
console.log('Competitor platforms:', competitorPlatforms)
console.log('All media accounts:', allMediaAccounts)
console.log('Relevant accounts:', relevantUserAccounts)
console.log('Loading:', accountsLoading)

// Auto-select primary accounts when platforms change
useEffect(() => {
  // Just clear selections when platforms change, let user manually select
  if (competitorPlatforms.length > 0 && relevantUserAccounts.length > 0) {
    setSelectedUserAccounts([])
  }
}, [competitorPlatforms.join(','), relevantUserAccounts.length])


const handleSubmit = async () => {
  if (!campaignName.trim()) {
    alert('Please enter a campaign name')
    return
  }
  if (competitors.length === 0) {
    alert('Please add at least one competitor')
    return
  }
  if (selectedUserAccounts.length === 0) {
    alert('Please select at least one of your accounts to track')
    return
  }

  try {
    
    const userAccountsInput = selectedUserAccounts.map(accountId => {
      const account = relevantUserAccounts.find(acc => acc.id === accountId)
      if (!account) throw new Error('Account not found')
      return {
        accountId: account.id,
        platform: account.platform
      }
    })


    const { data } = await createCampaign({  // ✅ Capture the result
      variables: {
        name: campaignName,
        hitsPerDay: 50,
        platforms: competitors.map(c => c.platform),
        followersInfo: {
          operator: 'None',
          from: 0,
          to: 0
        },
        campaignType: CampaignType.Competitor,
        competitorConfig: {
          autoRefreshEnabled: true,
          autoRefreshInterval,
          contentAnalysisEnabled,
          strategyDetection: true,
          gapAnalysis: true
        },
        competitorAccounts: competitors,
        userAccounts: userAccountsInput
      }
    })
    
    const campaignId = data?.campaignCreate?.id   // ✅ Use data directly

    if (campaignId) {
      // ✅ For competitor campaigns, skip loading screen and redirect immediately
      // Competitor data is collected during campaign creation (not through DataCollector queue)
      console.log('✅ Competitor campaign created successfully:', campaignId)
      navigate(`/campaigns/${campaignId}/competitors`)
    }
  } catch (error) {
    console.error('Error creating competitor campaign:', error)
    alert('Failed to create campaign. Please try again.')
  }
}

    const getPlatformIcon = (platform: Platform): string => {
      const icons: Partial<Record<Platform, string>> = {
        [Platform.Instagram]: '📷',
        [Platform.TikTok]: '🎵',
        [Platform.Youtube]: '🎥',
      }
      return icons[platform] ?? '📱'  // Fallback icon
    }

    const getPlatformColor = (platform: Platform): string => {
      const colors: Partial<Record<Platform, string>> = {
        [Platform.Instagram]: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
        [Platform.TikTok]: 'linear-gradient(135deg, #00f2ea 0%, #ff0050 100%)',
        [Platform.Youtube]: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
      }
      return colors[platform] ?? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'  // Fallback gray gradient
    }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        padding: '32px',
      }}
    >
      <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ marginBottom: '32px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Box
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f97316 0%, #eab308 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(249, 115, 22, 0.4)',
              }}
            >
              <Target style={{ width: '24px', height: '24px', color: 'white' }} />
            </Box>
            <Typography
              sx={{
                fontSize: '32px',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #f97316 0%, #eab308 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Competitor Analysis Campaign
            </Typography>
          </Box>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
            Track competitors and get AI-powered insights to grow your audience
          </Typography>
        </Box>

        {/* Main Container */}
        <Box
          sx={{
            background: 'rgba(30, 41, 59, 0.5)',
            backdropFilter: 'blur(24px)',
            borderRadius: '16px',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            padding: '32px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          }}
        >
          {/* Campaign Name Section */}
          <Box
            sx={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(168, 85, 247, 0.15)',
              marginBottom: '24px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <TrendingUp style={{ width: '20px', height: '20px', color: '#f97316' }} />
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#f97316',
                }}
              >
                Campaign Details
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Campaign Name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="e.g., Fitness Influencers Analysis"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(30, 41, 59, 0.5)',
                  color: '#ffffff',
                  '& fieldset': { borderColor: 'rgba(168, 85, 247, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.4)' },
                  '&.Mui-focused fieldset': { 
                    borderColor: '#f97316',
                    boxShadow: '0 0 0 3px rgba(249, 115, 22, 0.1)'
                  }
                },
                '& .MuiInputLabel-root': { 
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-focused': { color: '#f97316' }
                },
                '& .MuiInputBase-input': { color: '#ffffff' }
              }}
            />
          </Box>

          {/* Competitors Section */}
          <Box
            sx={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(168, 85, 247, 0.15)',
              marginBottom: '24px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <Users style={{ width: '20px', height: '20px', color: '#a855f7' }} />
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#a855f7',
                }}
              >
                Competitors to Track
              </Typography>
              {competitors.length > 0 && (
                <Chip
                  label={`${competitors.length} added`}
                  size="small"
                  sx={{
                    marginLeft: 'auto',
                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.3) 0%, rgba(236, 72, 153, 0.3) 100%)',
                    color: '#a855f7',
                    fontWeight: 600,
                    border: '1px solid rgba(168, 85, 247, 0.3)',
                  }}
                />
              )}
            </Box>
            
            {/* Add Competitor Form */}
            <Box sx={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel 
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-focused': { color: '#a855f7' }
                  }}
                >
                  Platform
                </InputLabel>
                <Select
                  value={newCompetitor.platform}
                  onChange={(e) => setNewCompetitor({ ...newCompetitor, platform: e.target.value as Platform })}
                  label="Platform"
                  sx={{
                    backgroundColor: 'rgba(30, 41, 59, 0.5)',
                    color: '#ffffff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168, 85, 247, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168, 85, 247, 0.4)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                      borderColor: '#a855f7',
                      boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.1)'
                    },
                    '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(168, 85, 247, 0.2)',
                        '& .MuiMenuItem-root': {
                          color: '#ffffff',
                          '&:hover': {
                            backgroundColor: 'rgba(168, 85, 247, 0.1)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(168, 85, 247, 0.2)',
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value={Platform.Instagram}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>📷</span> Instagram
                    </Box>
                  </MenuItem>
                  <MenuItem value={Platform.TikTok}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>🎵</span> TikTok
                    </Box>
                  </MenuItem>
                  <MenuItem value={Platform.Youtube}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>🎥</span> YouTube
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                sx={{ flex: 1, minWidth: '250px' }}
                label="Username"
                value={newCompetitor.username}
                onChange={(e) => setNewCompetitor({ ...newCompetitor, username: e.target.value })}
                placeholder="@competitor"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddCompetitor()
                  }
                }}
                InputProps={{
                  sx: {
                    backgroundColor: 'rgba(30, 41, 59, 0.5)',
                    color: '#ffffff',
                    '& fieldset': { borderColor: 'rgba(168, 85, 247, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.4)' },
                    '&.Mui-focused fieldset': { 
                      borderColor: '#a855f7',
                      boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.1)'
                    }
                  }
                }}
                InputLabelProps={{
                  sx: { 
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-focused': { color: '#a855f7' }
                  }
                }}
              />

              <Button
                variant="contained"
                onClick={handleAddCompetitor}
                disabled={!newCompetitor.username.trim()}
                sx={{
                  background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
                  minWidth: '120px',
                  height: '56px',
                  boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #a855f7 0%, #f472b6 100%)',
                    transform: 'scale(1.02)',
                    boxShadow: '0 6px 20px rgba(147, 51, 234, 0.4)',
                  },
                  '&:disabled': {
                    background: 'rgba(100, 100, 100, 0.3)',
                    color: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              >
                <Plus style={{ width: '20px', height: '20px', marginRight: '8px' }} />
                Add
              </Button>
            </Box>

            {/* Competitor List */}
            {competitors.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {competitors.map((comp, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 20px',
                      background: 'rgba(30, 41, 59, 0.5)',
                      borderRadius: '12px',
                      border: '1px solid rgba(168, 85, 247, 0.15)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: 'rgba(30, 41, 59, 0.8)',
                        borderColor: 'rgba(168, 85, 247, 0.3)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <Box
                        sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: getPlatformColor(comp.platform),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                        }}
                      >
                        {getPlatformIcon(comp.platform)}
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '15px' }}>
                          {comp.username}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
                          {comp.platform}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      onClick={() => handleRemoveCompetitor(index)}
                      sx={{
                        color: 'rgba(239, 68, 68, 0.7)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          color: '#ef4444',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          transform: 'scale(1.1)',
                        }
                      }}
                    >
                      <Trash2 style={{ width: '20px', height: '20px' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ 
                padding: '40px 24px', 
                textAlign: 'center',
                background: 'rgba(30, 41, 59, 0.3)',
                borderRadius: '12px',
                border: '2px dashed rgba(168, 85, 247, 0.2)'
              }}>
                <Users style={{ width: '48px', height: '48px', color: 'rgba(168, 85, 247, 0.3)', margin: '0 auto 12px' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                  No competitors added yet. Add your first competitor above.
                </Typography>
              </Box>
            )}
          </Box>
          {/* NEW: Your Accounts Selection Section */}
          {competitors.length > 0 && (
            <Box
              sx={{
                background: 'rgba(15, 23, 42, 0.5)',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid rgba(168, 85, 247, 0.15)',
                marginBottom: '24px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <LinkIcon style={{ width: '20px', height: '20px', color: '#10b981' }} />
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#10b981',
                  }}
                >
                  Your Accounts to Track
                </Typography>
                {selectedUserAccounts.length > 0 && ( 
                  <Chip
                    label={`${selectedUserAccounts.length} selected`}
                    size="small"
                    sx={{
                      marginLeft: 'auto',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.3) 100%)',
                      color: '#10b981',
                      fontWeight: 600,
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                    }}
                  />
                )}
              </Box>

              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '16px' }}>
                Select which of your connected accounts to compare against competitors
              </Typography>

              {accountsLoading ? (
                <Box sx={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.5)' }}>
                  Loading your accounts...
                </Box>
              ) : relevantUserAccounts.length === 0 ? (
                <Alert 
                  severity="warning"
                  sx={{
                    background: 'rgba(234, 179, 8, 0.1)',
                    border: '1px solid rgba(234, 179, 8, 0.3)',
                    color: '#fbbf24',
                    '& .MuiAlert-icon': { color: '#fbbf24' }
                  }}
                >
                  You don&apos;t have any connected accounts for {competitorPlatforms.join(', ')}. 
                  Please connect your accounts in Settings first.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {relevantUserAccounts.map((account) => (
                    <Box
                      key={account.id}
                      onClick={() => handleToggleUserAccount(account.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 20px',
                        background: selectedUserAccounts.includes(account.id)
                          ? 'rgba(16, 185, 129, 0.15)'
                          : 'rgba(30, 41, 59, 0.5)',
                        borderRadius: '12px',
                        border: selectedUserAccounts.includes(account.id)
                          ? '2px solid rgba(16, 185, 129, 0.5)'
                          : '1px solid rgba(168, 85, 247, 0.15)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: selectedUserAccounts.includes(account.id)
                            ? 'rgba(16, 185, 129, 0.2)'
                            : 'rgba(30, 41, 59, 0.8)',
                          borderColor: selectedUserAccounts.includes(account.id)
                            ? 'rgba(16, 185, 129, 0.6)'
                            : 'rgba(168, 85, 247, 0.3)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Checkbox
                          checked={selectedUserAccounts.includes(account.id)}
                          sx={{
                            color: 'rgba(168, 85, 247, 0.5)',
                            '&.Mui-checked': {
                              color: '#10b981',
                            }
                          }}
                        />
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            border: '2px solid rgba(16, 185, 129, 0.3)',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            fontSize: '16px',
                            fontWeight: 600,
                          }}
                        >
                          {account.accountName.charAt(0).toUpperCase()}
                        </Box>
                        <Box>
                        <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '15px' }}>
                          {account.accountName}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
                          {account.platform}
                        </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: getPlatformColor(account.platform),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                        }}
                      >
                        {getPlatformIcon(account.platform)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Settings Section */}
          <Box
            sx={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(168, 85, 247, 0.15)',
              marginBottom: '24px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
              <Settings style={{ width: '20px', height: '20px', color: '#a855f7' }} />
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#a855f7',
                }}
              >
                Campaign Settings
              </Typography>
            </Box>

            {/* Auto-Refresh Interval */}
            <Box sx={{ marginBottom: '24px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Clock style={{ width: '16px', height: '16px', color: 'rgba(255, 255, 255, 0.7)' }} />
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '15px', fontWeight: 500 }}>
                  Auto-Refresh Interval
                </Typography>
              </Box>
              <FormControl fullWidth>
                <Select
                  value={autoRefreshInterval}
                  onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
                  sx={{
                    backgroundColor: 'rgba(30, 41, 59, 0.5)',
                    color: '#ffffff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168, 85, 247, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168, 85, 247, 0.4)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                      borderColor: '#a855f7',
                      boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.1)'
                    },
                    '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(168, 85, 247, 0.2)',
                        '& .MuiMenuItem-root': {
                          color: '#ffffff',
                          '&:hover': {
                            backgroundColor: 'rgba(168, 85, 247, 0.1)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(168, 85, 247, 0.2)',
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value={30}>Every 30 minutes (Enterprise)</MenuItem>
                  <MenuItem value={120}>Every 2 hours (Business)</MenuItem>
                  <MenuItem value={360}>Every 6 hours (Basic)</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '8px' }}>
                How often to automatically fetch new competitor data
              </Typography>
            </Box>

            {/* AI Content Analysis Toggle */}
            <Box
              sx={{
                padding: '16px 20px',
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.15)',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={contentAnalysisEnabled}
                    onChange={(e) => setContentAnalysisEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#a855f7',
                        '&:hover': {
                          backgroundColor: 'rgba(168, 85, 247, 0.08)',
                        }
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#a855f7',
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Sparkles style={{ width: '18px', height: '18px', color: '#a855f7' }} />
                    <Box>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500, fontSize: '15px' }}>
                        AI Content Analysis
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                        Use AI to analyze competitor strategies and generate insights
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ margin: 0, width: '100%' }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '16px', paddingTop: '8px' }}>
            <Button
              variant="outlined"
              onClick={() => navigate(pages.campaigns)}
              sx={{
                flex: '0 0 150px',
                padding: '14px 24px',
                color: 'rgba(239, 68, 68, 0.9)',
                borderColor: 'rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: 600,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#ef4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { handleSubmit().catch(console.error) }}
              disabled={
                loading || 
                competitors.length === 0 || 
                !campaignName.trim() ||
                selectedUserAccounts.length === 0  // This validation only applies to competitor campaigns
              }
              sx={{
                padding: '14px 32px',
                background: loading ? 'rgba(100, 100, 100, 0.3)' : 'linear-gradient(135deg, #f97316 0%, #eab308 100%)',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: 600,
                boxShadow: loading ? 'none' : '0 4px 12px rgba(249, 115, 22, 0.3)',
                transition: 'all 0.2s',
                '&:hover': {
                  background: loading ? 'rgba(100, 100, 100, 0.3)' : 'linear-gradient(135deg, #fb923c 0%, #fde047 100%)',
                  transform: loading ? 'none' : 'scale(1.02)',
                  boxShadow: loading ? 'none' : '0 6px 20px rgba(249, 115, 22, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(100, 100, 100, 0.3)',
                  color: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              {loading ? 'Creating Campaign...' : 'Create Competitor Campaign'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}